/**
 * Cross-sell Popup Component
 * Shows recommended products when a product is added to cart from the product page
 */

class CrosssellPopup extends HTMLElement {
  constructor() {
    super();
    this.modal = null;
    this.recommendationsCache = new Map();
    this.cacheDuration = 5 * 60 * 1000; // 5 minutes
    this.isProcessing = false;
    this.addToCartDebounce = null;
  }

  connectedCallback() {
    this.config = {
      sectionId: this.dataset.sectionId,
      heading: this.dataset.heading || 'Complete your order',
      maxProducts: parseInt(this.dataset.maxProducts, 10) || 4,
      popupWidth: this.dataset.popupWidth || '600px',
      buttonText: this.dataset.buttonText || 'Add',
      continueText: this.dataset.continueText || 'Continue shopping',
      colorScheme: this.dataset.colorScheme || 'default-color-scheme',
      rtl: this.dataset.rtl === 'true',
      soldOutText: window.variantStrings?.soldOut || 'Sold out'
    };

    this.subscribeToCartUpdate();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  subscribeToCartUpdate() {
    console.log('CrosssellPopup: Initializing...');
    console.log('CrosssellPopup: subscribe available:', typeof subscribe === 'function');
    console.log('CrosssellPopup: PUB_SUB_EVENTS available:', typeof PUB_SUB_EVENTS !== 'undefined');

    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      console.warn('CrosssellPopup: PubSub system not available');
      return;
    }

    console.log('CrosssellPopup: Subscribing to cartUpdate event');
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      console.log('CrosssellPopup: cartUpdate event received', event);
      this.handleCartUpdate(event);
    });
  }

  handleCartUpdate(event) {
    console.log('CrosssellPopup: handleCartUpdate called');
    console.log('CrosssellPopup: event.source =', event.source);
    console.log('CrosssellPopup: isProductPage =', this.isProductPage());

    // Only trigger on product-form source (product page add to cart)
    if (event.source !== 'product-form') {
      console.log('CrosssellPopup: Skipping - not from product-form');
      return;
    }

    // Only trigger on product pages
    if (!this.isProductPage()) {
      console.log('CrosssellPopup: Skipping - not on product page');
      return;
    }

    // Prevent double-processing
    if (this.isProcessing) {
      console.log('CrosssellPopup: Skipping - already processing');
      return;
    }

    // Set global flag to block cart drawer
    window.crosssellPopupBlocking = true;
    console.log('CrosssellPopup: Set blocking flag');

    const productId = this.getCurrentProductId();
    console.log('CrosssellPopup: productId =', productId);

    if (!productId) {
      console.log('CrosssellPopup: No product ID found');
      window.crosssellPopupBlocking = false;
      return;
    }

    this.isProcessing = true;
    console.log('CrosssellPopup: Fetching recommendations...');
    this.fetchAndShowRecommendations(productId);
  }

  isProductPage() {
    // Check if we're on a product page via template or URL
    const template = document.body.dataset.template;
    if (template && template.includes('product')) {
      return true;
    }

    // Fallback: check URL pattern
    return window.location.pathname.includes('/products/');
  }

  getCurrentProductId() {
    // Try to get product ID from various sources on the page

    // Method 1: From product JSON script
    const productJsonScript = document.querySelector('[type="application/json"][data-product-json]');
    if (productJsonScript) {
      try {
        const product = JSON.parse(productJsonScript.textContent);
        if (product.id) return product.id;
      } catch (e) {
        // Silent fail
      }
    }

    // Method 2: From main product form's product ID
    const productIdMeta = document.querySelector('meta[itemprop="productID"]');
    if (productIdMeta) {
      return productIdMeta.content;
    }

    // Method 3: From product data attribute
    const productWrapper = document.querySelector('[data-product-id]');
    if (productWrapper && productWrapper.dataset.productId) {
      return productWrapper.dataset.productId;
    }

    // Method 4: From product form section
    const productFormSection = document.querySelector('.shopify-section-template--main-product, .shopify-section-main-product');
    if (productFormSection) {
      const productIdEl = productFormSection.querySelector('[data-product-id]');
      if (productIdEl) return productIdEl.dataset.productId;
    }

    // Method 5: Extract handle from URL and use it (recommendations API accepts handles)
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (match) {
      // We'll need to convert handle to ID, but for now we can use the recommendations HTML endpoint
      return match[1];
    }

    return null;
  }

  async fetchAndShowRecommendations(productIdOrHandle) {
    try {
      console.log('CrosssellPopup: fetchAndShowRecommendations for', productIdOrHandle);

      // Check cache first
      const cached = this.getCachedRecommendations(productIdOrHandle);
      if (cached && cached.length > 0) {
        console.log('CrosssellPopup: Using cached recommendations', cached);
        this.showPopup(cached);
        return;
      }

      // First try to get products from metafield data (manually selected)
      let products = this.getMetafieldProducts();

      if (products && products.length > 0) {
        console.log('CrosssellPopup: Using metafield products', products);
      } else {
        // Fallback to Shopify's product recommendations API
        console.log('CrosssellPopup: No metafield data, fetching from API...');
        products = await this.fetchShopifyRecommendations(productIdOrHandle);
        console.log('CrosssellPopup: API returned products', products);
      }

      if (products && products.length > 0) {
        this.cacheRecommendations(productIdOrHandle, products);
        console.log('CrosssellPopup: Showing popup with', products.length, 'products');
        this.showPopup(products);
      } else {
        // No recommendations, allow cart drawer to open
        console.log('CrosssellPopup: No recommendations found, opening cart drawer');
        window.crosssellPopupBlocking = false;
        this.isProcessing = false;
      }
    } catch (error) {
      console.error('CrosssellPopup: Error fetching recommendations', error);
      window.crosssellPopupBlocking = false;
      this.isProcessing = false;
    }
  }

  getMetafieldProducts() {
    try {
      const metafieldScript = document.getElementById('crosssell-metafield-data');
      if (!metafieldScript) {
        console.log('CrosssellPopup: No metafield data element found');
        return null;
      }

      const data = JSON.parse(metafieldScript.textContent);
      console.log('CrosssellPopup: Metafield data found', data);

      if (data.products && data.products.length > 0) {
        // Limit to max products setting
        return data.products.slice(0, this.config.maxProducts);
      }

      return null;
    } catch (error) {
      console.error('CrosssellPopup: Error parsing metafield data', error);
      return null;
    }
  }

  async fetchShopifyRecommendations(productIdOrHandle) {
    try {
      // Try JSON API first for full product data
      let url = `/recommendations/products.json?product_id=${productIdOrHandle}&limit=${this.config.maxProducts}&intent=complementary`;
      let response = await fetch(url);

      if (!response.ok) {
        // Try without intent parameter
        url = `/recommendations/products.json?product_id=${productIdOrHandle}&limit=${this.config.maxProducts}`;
        response = await fetch(url);
      }

      if (!response.ok) {
        // If numeric ID fails, it might be a handle - try section rendering approach
        if (!/^\d+$/.test(productIdOrHandle)) {
          return await this.fetchRecommendationsViaHTML(productIdOrHandle);
        }
        return null;
      }

      const data = await response.json();

      if (!data.products || data.products.length === 0) {
        return null;
      }

      // Return full product data for client-side rendering
      return data.products.slice(0, this.config.maxProducts);
    } catch (error) {
      console.error('CrosssellPopup: API fetch failed', error);
      return null;
    }
  }

  async fetchRecommendationsViaHTML(productHandle) {
    try {
      // Fetch the product page to get its ID, then fetch recommendations
      const productResponse = await fetch(`/products/${productHandle}.json`);
      if (!productResponse.ok) return null;

      const productData = await productResponse.json();
      const productId = productData.product?.id;

      if (!productId) return null;

      // Now fetch recommendations with the actual ID
      const url = `/recommendations/products.json?product_id=${productId}&limit=${this.config.maxProducts}`;
      const response = await fetch(url);

      if (!response.ok) return null;

      const data = await response.json();
      return data.products?.slice(0, this.config.maxProducts) || null;
    } catch (error) {
      return null;
    }
  }

  getCachedRecommendations(productId) {
    const cached = this.recommendationsCache.get(productId);
    if (cached && Date.now() - cached.timestamp < this.cacheDuration) {
      return cached.products;
    }
    return null;
  }

  cacheRecommendations(productId, products) {
    this.recommendationsCache.set(productId, {
      products,
      timestamp: Date.now()
    });
  }

  showPopup(products) {
    try {
      console.log('CrosssellPopup: showPopup called with', products.length, 'products');
      const content = this.renderPopupContent(products);
      console.log('CrosssellPopup: Content rendered, opening modal');
      this.openModal(content);
    } catch (error) {
      console.error('CrosssellPopup: Error showing popup', error);
      window.crosssellPopupBlocking = false;
      this.isProcessing = false;
    }
  }

  renderPopupContent(products) {
    const isSingleProduct = products.length === 1;
    const productsHtml = products.map(product => this.renderProductCard(product, isSingleProduct)).join('');
    const productsClass = isSingleProduct ? 'crosssell-popup__products crosssell-popup__products--single' : 'crosssell-popup__products';
    const contentClass = isSingleProduct ? 'crosssell-popup__content crosssell-popup__content--single' : 'crosssell-popup__content';

    // For single product, show add button in footer as full width
    let footerHtml;
    if (isSingleProduct) {
      const product = products[0];
      const firstVariant = product.variants?.[0] || {};
      const isAvailable = firstVariant.available !== false && product.available !== false;
      const variantId = firstVariant.id || product.variants?.[0]?.id;

      if (isAvailable && variantId) {
        footerHtml = `
          <div class="crosssell-popup__footer">
            <button
              type="button"
              class="crosssell-popup__add-btn crosssell-popup__add-btn--full btn btn-primary"
              data-variant-id="${variantId}"
              data-product-id="${product.id}"
            >
              <span class="crosssell-popup__btn-text">${this.escapeHtml(this.config.buttonText)} - ${this.formatMoney(product.price || firstVariant.price)}</span>
              <span class="crosssell-popup__btn-loading hidden">
                <svg class="spinner" viewBox="0 0 24 24" width="20" height="20">
                  <circle class="spinner-circle" cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"></circle>
                </svg>
              </span>
              <span class="crosssell-popup__btn-added hidden">
                <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
                  <path d="M1 7L5 11L13 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                Aggiunto!
              </span>
            </button>
            <button type="button" class="crosssell-popup__continue crosssell-popup__continue--secondary">
              ${this.escapeHtml(this.config.continueText)}
            </button>
          </div>
        `;
      } else {
        footerHtml = `
          <div class="crosssell-popup__footer">
            <button type="button" class="crosssell-popup__add-btn crosssell-popup__add-btn--full btn btn-secondary" disabled>
              <span class="crosssell-popup__btn-text">${this.escapeHtml(this.config.soldOutText)}</span>
            </button>
            <button type="button" class="crosssell-popup__continue crosssell-popup__continue--secondary">
              ${this.escapeHtml(this.config.continueText)}
            </button>
          </div>
        `;
      }
    } else {
      footerHtml = `
        <div class="crosssell-popup__footer">
          <button type="button" class="crosssell-popup__continue btn btn-outline w-full">
            ${this.escapeHtml(this.config.continueText)}
          </button>
        </div>
      `;
    }

    return `
      <div class="${contentClass} gradient color-${this.config.colorScheme}">
        <div class="crosssell-popup__header">
          <h3 class="crosssell-popup__heading heading-letter-spacing mt-0 mb-0">${this.escapeHtml(this.config.heading)}</h3>
        </div>
        <div class="${productsClass}">
          ${productsHtml}
        </div>
        ${footerHtml}
      </div>
    `;
  }

  renderProductCard(product, isSingleProduct = false) {
    const firstVariant = product.variants?.[0] || {};
    const isAvailable = firstVariant.available !== false && product.available !== false;
    const variantId = firstVariant.id || product.variants?.[0]?.id;

    // Price formatting
    const price = this.formatMoney(product.price || firstVariant.price);
    const comparePrice = product.compare_at_price || firstVariant.compare_at_price;
    const hasDiscount = comparePrice && comparePrice > (product.price || firstVariant.price);

    // Image - use larger size for single product
    const imageSize = isSingleProduct ? '400x' : '200x';
    const imageUrl = product.featured_image || product.images?.[0] || '';
    const imageHtml = imageUrl
      ? `<img src="${this.getImageUrl(imageUrl, imageSize)}" alt="${this.escapeHtml(product.title)}" class="crosssell-popup__image" loading="lazy">`
      : `<div class="crosssell-popup__image placeholder-svg"><svg viewBox="0 0 525 525"><rect width="525" height="525" fill="#f0f0f0"/></svg></div>`;

    // Price HTML
    let priceHtml;
    if (hasDiscount) {
      priceHtml = `
        <span class="crosssell-popup__price--sale">${price}</span>
        <span class="crosssell-popup__price--compare"><s>${this.formatMoney(comparePrice)}</s></span>
      `;
    } else {
      priceHtml = `<span class="crosssell-popup__price">${price}</span>`;
    }

    // Button HTML
    let buttonHtml;
    if (isAvailable && variantId) {
      buttonHtml = `
        <button
          type="button"
          class="crosssell-popup__add-btn btn btn-primary"
          data-variant-id="${variantId}"
          data-product-id="${product.id}"
        >
          <span class="crosssell-popup__btn-text">${this.escapeHtml(this.config.buttonText)}</span>
          <span class="crosssell-popup__btn-loading hidden">
            <svg class="spinner" viewBox="0 0 24 24" width="18" height="18">
              <circle class="spinner-circle" cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"></circle>
            </svg>
          </span>
          <span class="crosssell-popup__btn-added hidden">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 7L5 11L13 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </button>
      `;
    } else {
      buttonHtml = `
        <button type="button" class="crosssell-popup__add-btn btn btn-secondary" disabled>
          <span class="crosssell-popup__btn-text">${this.escapeHtml(this.config.soldOutText)}</span>
        </button>
      `;
    }

    const productClass = isSingleProduct
      ? 'crosssell-popup__product crosssell-popup__product--single'
      : 'crosssell-popup__product';

    return `
      <div class="${productClass}" data-product-id="${product.id}" data-variant-id="${variantId || ''}">
        <div class="crosssell-popup__product-image">
          <a href="${product.url || '/products/' + product.handle}" class="crosssell-popup__product-link">
            ${imageHtml}
          </a>
        </div>
        <div class="crosssell-popup__product-info">
          <a href="${product.url || '/products/' + product.handle}" class="crosssell-popup__product-title no-underline">
            ${this.escapeHtml(product.title)}
          </a>
          <div class="crosssell-popup__product-price">
            ${priceHtml}
          </div>
        </div>
        <div class="crosssell-popup__product-action">
          ${buttonHtml}
        </div>
      </div>
    `;
  }

  getImageUrl(url, size) {
    if (!url) return '';
    // Handle Shopify CDN URLs
    if (url.includes('cdn.shopify.com')) {
      return url.replace(/(_\d+x\d*|\d+x\d*)?(\.[^.]+)$/, `_${size}$2`);
    }
    return url;
  }

  formatMoney(cents) {
    if (typeof cents === 'undefined' || cents === null) return '';

    // Use Shopify's money format if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '€{{amount_with_comma_separator}}');
    }

    // Fallback: simple Euro formatting
    const amount = (cents / 100).toFixed(2);
    return '€' + amount.replace('.', ',');
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  openModal(content) {
    console.log('CrosssellPopup: openModal called');
    console.log('CrosssellPopup: tingle available:', typeof tingle !== 'undefined');

    if (typeof tingle === 'undefined') {
      console.error('CrosssellPopup: tingle.js not loaded');
      window.crosssellPopupBlocking = false;
      this.isProcessing = false;
      return;
    }

    const _this = this;

    this.modal = new tingle.modal({
      footer: false,
      stickyFooter: false,
      closeMethods: ['overlay', 'button', 'escape'],
      cssClass: ['crosssell-popup-modal'],
      onOpen: function() {
        const modalBox = this.modal.querySelector('.tingle-modal-box');
        if (modalBox) {
          modalBox.style.setProperty('--popup-max-width', _this.config.popupWidth);
        }
        document.dispatchEvent(new CustomEvent('modal:opened'));
        document.documentElement.classList.add('open-popup', 'open-crosssell-popup');

        const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.setProperty('padding-right', scrollBarWidth + 'px');

        // Bind event handlers
        _this.bindPopupEvents();
      },
      onClose: function() {
        document.dispatchEvent(new CustomEvent('modal:closed'));
        document.documentElement.classList.remove('open-popup', 'open-crosssell-popup');
        document.body.style.removeProperty('padding-right');

        window.crosssellPopupBlocking = false;
        _this.isProcessing = false;
      },
      beforeClose: function() {
        document.documentElement.classList.remove('open-popup', 'open-crosssell-popup');
        document.body.style.removeProperty('padding-right');
        return true;
      }
    });

    this.modal.setContent(content);
    this.modal.open();
  }

  bindPopupEvents() {
    if (!this.modal) return;

    const modalContent = this.modal.modal;

    // Continue shopping buttons (can be multiple with different styles)
    const continueBtns = modalContent.querySelectorAll('.crosssell-popup__continue');
    continueBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        this.modal.close();
      });
    });

    // Add to cart buttons (including full-width button)
    const addBtns = modalContent.querySelectorAll('.crosssell-popup__add-btn:not([disabled])');
    addBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.handleAddToCart(e, btn);
      });
    });
  }

  async handleAddToCart(event, button) {
    event.preventDefault();

    // Debounce to prevent double clicks
    if (this.addToCartDebounce) {
      return;
    }
    this.addToCartDebounce = true;
    setTimeout(() => {
      this.addToCartDebounce = false;
    }, 1000);

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    // Show loading state
    this.setButtonState(button, 'loading');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      // Show success state with checkmark
      this.setButtonState(button, 'added');

      // Wait for "Aggiunto" animation to complete, then close and open cart
      setTimeout(() => {
        this.modal.close();

        // After popup closes, open cart drawer
        setTimeout(() => {
          this.openCartDrawer();
        }, 400);
      }, 1200);

    } catch (error) {
      console.error('CrosssellPopup: Add to cart failed', error);
      this.setButtonState(button, 'default');
    }
  }

  setButtonState(button, state) {
    const textEl = button.querySelector('.crosssell-popup__btn-text');
    const loadingEl = button.querySelector('.crosssell-popup__btn-loading');
    const addedEl = button.querySelector('.crosssell-popup__btn-added');

    button.classList.remove('loading', 'added');

    if (textEl) textEl.classList.remove('hidden');
    if (loadingEl) loadingEl.classList.add('hidden');
    if (addedEl) addedEl.classList.add('hidden');

    switch (state) {
      case 'loading':
        button.classList.add('loading');
        button.disabled = true;
        if (textEl) textEl.classList.add('hidden');
        if (loadingEl) loadingEl.classList.remove('hidden');
        break;
      case 'added':
        button.classList.add('added');
        button.disabled = true;
        if (textEl) textEl.classList.add('hidden');
        if (addedEl) addedEl.classList.remove('hidden');
        break;
      default:
        button.disabled = false;
    }
  }

  openCartDrawer() {
    // Try to open the cart drawer/notification
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification && typeof cartNotification.open === 'function') {
      // Refresh cart content first, then open
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          // Update cart sections if available
          const sectionsToRender = cartNotification.getSectionsToRender?.() || [];
          if (sectionsToRender.length > 0) {
            const sectionIds = sectionsToRender.map(s => s.id).join(',');
            return fetch(`/cart?sections=${sectionIds}`);
          }
          return null;
        })
        .then(res => res?.json())
        .then(sections => {
          if (sections) {
            // Update minicart content
            const minicartForm = document.getElementById('minicart-form');
            if (minicartForm && sections['minicart-form']) {
              const html = new DOMParser().parseFromString(sections['minicart-form'], 'text/html');
              const newContent = html.querySelector('#minicart-form');
              if (newContent) {
                minicartForm.innerHTML = newContent.innerHTML;
              }
            }
          }
          // Update cart count
          this.updateCartCount();
          // Open the cart drawer
          cartNotification.open();
        })
        .catch(() => {
          // Fallback: just open
          cartNotification.open();
        });
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.json');
      const cart = await response.json();

      if (cart.item_count !== undefined) {
        document.querySelectorAll('.cart-count').forEach(el => {
          if (el.classList.contains('cart-count-drawer')) {
            el.innerHTML = `(${cart.item_count})`;
          } else {
            el.innerHTML = cart.item_count > 100 ? '~' : cart.item_count;
          }
        });

        // Update header total if exists
        const headerTotal = document.querySelector('header-total-price');
        if (headerTotal && typeof headerTotal.updateTotal === 'function') {
          headerTotal.updateTotal(cart);
        }

        // Update free shipping bar if exists
        const freeShipBar = document.querySelector('free-ship-progress-bar');
        if (freeShipBar && typeof freeShipBar.init === 'function') {
          freeShipBar.init(cart.items_subtotal_price);
        }
      }
    } catch (error) {
      console.error('CrosssellPopup: Failed to update cart count', error);
    }
  }
}

// Register the custom element
if (!customElements.get('crosssell-popup')) {
  customElements.define('crosssell-popup', CrosssellPopup);
  console.log('CrosssellPopup: Custom element registered');
}
